import React, {useEffect, useState} from 'react';
import Swal from 'sweetalert2';
import {Download, FileText, AlertCircle, Trash2, Pencil, Check, X} from 'lucide-react';
import pngIcon from "../../assets/types/png.png";
import pdfIcon from "../../assets/types/pdf.png";
import msOfficeIcon from "../../assets/types/ms-office.png";
import jpgIcon from "../../assets/types/jpg.png";
import zipIcon from "../../assets/types/zip.png";
import xlsxIcon from "../../assets/types/xlsx.png";
import defaultIcon from "../../assets/types/default.png";
import useApi from "../../utils/useApi";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import clsx from "clsx";
import Button from "../UI/Button";
import {useNavigate} from "react-router-dom";

const FormList = ({user, handleShowHeader, handleShowSettingsHeader}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [forms, setForms] = useState(null);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingComment, setEditingComment] = useState("");
    const [editingFileNameId, setEditingFileNameId] = useState(null);
    const [editingFileName, setEditingFileName] = useState("");
    const { getForms, deleteForm, updateForm } = useApi();

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initializeForms();
    }, []);

    const initializeForms = async () => {
        const response = await getForms();
        setForms(response.documents);
        setLoading(false);
    }

    // Comment editing handlers
    const handleEditComment = (form) => {
        setEditingCommentId(form.id);
        setEditingComment(form.document_comments || "");
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingComment("");
    };

    const handleSaveComment = async (formId) => {
        try {
            await updateForm(formId, { document_comments: editingComment });
            await Swal.fire({
                                title: "Success!",
                                text: "Comment updated successfully",
                                icon: "success",
                                confirmButtonColor: "#198754"
                            });
            setEditingCommentId(null);
            initializeForms();
        } catch (error) {
            await Swal.fire({
                                title: "Error",
                                text: error.message || "An error occurred while updating the comment",
                                icon: "error",
                                confirmButtonColor: "#dc3545"
                            });
        }
    };

    // File name editing handlers
    const handleEditFileName = (form) => {
        setEditingFileNameId(form.id);
        setEditingFileName(form.file_name || "");
    };

    const handleCancelFileNameEdit = () => {
        setEditingFileNameId(null);
        setEditingFileName("");
    };

    const handleSaveFileName = async (formId) => {
        try {
            await updateForm(formId, { file_name: editingFileName });
            await Swal.fire({
                                title: "Success!",
                                text: "File name updated successfully",
                                icon: "success",
                                confirmButtonColor: "#198754"
                            });
            setEditingFileNameId(null);
            initializeForms();
        } catch (error) {
            await Swal.fire({
                                title: "Error",
                                text: error.message || "An error occurred while updating the file name",
                                icon: "error",
                                confirmButtonColor: "#dc3545"
                            });
        }
    };

    const handleDeleteForm = async (formId, event) => {
        event.preventDefault();
        try {
            const result = await Swal.fire({
                                               title: "Are you sure?",
                                               text: "This worksheet will be permanently deleted!",
                                               icon: "warning",
                                               showCancelButton: true,
                                               confirmButtonColor: "#dc3545",
                                               cancelButtonColor: "#6c757d",
                                               confirmButtonText: "Yes, delete it!",
                                               cancelButtonText: "Cancel",
                                               reverseButtons: true
                                           });

            if (result.isConfirmed) {
                await deleteForm(formId);
                await Swal.fire({
                                    title: "Deleted!",
                                    text: "The worksheet has been deleted.",
                                    icon: "success",
                                    confirmButtonColor: "#198754"
                                });
                initializeForms();
            }
        } catch (error) {
            console.error('Error deleting form:', error);
            await Swal.fire({
                                title: "Error",
                                text: error.message || "An error occurred while deleting the worksheet",
                                icon: "error",
                                confirmButtonColor: "#dc3545"
                            });
        }
    };

    const getFileIcon = (category) => {
        switch (category?.toLowerCase()) {
            case 'png': return pngIcon;
            case 'pdf': return pdfIcon;
            case 'docx': return msOfficeIcon;
            case 'jpg':
            case 'jpeg': return jpgIcon;
            case 'zip': return zipIcon;
            case 'xlsx': return xlsxIcon;
            default: return defaultIcon;
        }
    };

    const navigateToNewForm = () => {
        navigate('/admin/new-worksheet');
    }

    if(loading) return <LoadingScreen />;

    if (!forms || forms.length === 0) {
        return (
            <div className="flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg z-10 page-container">
                <AlertCircle className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2"/>
                <span className="text-gray-500 dark:text-gray-400">No forms available</span>
            </div>
        );
    }

    return (
        <div className="w-full z-10 relative md:p-16 p-6 bg-white dark:bg-gray-900 page-container">
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className={clsx('text-3xl font-bold dark:text-white text-gray-900')}>
                        Worksheets
                    </h1>
                </div>
                {user.user_type === 'admin' && (
                    <div>
                        <Button variant={'secondary'} onClick={navigateToNewForm}>
                            Add New Worksheet
                        </Button>
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {forms.map((form) => (
                    <div
                        key={form.id}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/10 p-4
                                 transition-all hover:shadow-lg dark:hover:shadow-gray-900/20
                                 border border-gray-100 dark:border-gray-700 relative"
                    >
                        {user.user_type === 'admin' && (
                            <button
                                onClick={(e) => handleDeleteForm(form.id, e)}
                                className="absolute top-1 right-1 p-2 text-gray-400 hover:text-red-500
                                         dark:text-gray-500 dark:hover:text-red-400
                                         transition-colors rounded-full
                                         hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="Delete Form"
                            >
                                <Trash2 className="w-4 h-4"/>
                            </button>
                        )}

                        {/* Form Header */}
                        <div className="flex items-center gap-3 mb-4">
                            {editingFileNameId !== form.id && (
                                <img
                                    src={getFileIcon(form.category)}
                                    alt={`${form.category} file`}
                                    className="w-12 h-12 object-contain"
                                />
                            )}

                            <div className="flex-1 min-w-0 flex items-start gap-2">
                                {editingFileNameId === form.id ? (
                                    <div className="flex-1 flex flex-col gap-2">
                                        <input
                                            type="text"
                                            value={editingFileName}
                                            onChange={(e) => setEditingFileName(e.target.value)}
                                            className="w-full p-1 text-sm border rounded
                                                     dark:bg-gray-800 dark:border-gray-600
                                                     dark:text-gray-300 focus:outline-none
                                                     focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleSaveFileName(form.id)}
                                                className="p-1 text-green-600 hover:text-green-700
                                                         dark:text-green-500 dark:hover:text-green-400"
                                                title="Save"
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={handleCancelFileNameEdit}
                                                className="p-1 text-gray-500 hover:text-gray-600
                                                         dark:text-gray-400 dark:hover:text-gray-300"
                                                title="Cancel"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                     <>
                                         <h3 className="text-sm text-gray-900 dark:text-gray-100 mt-1 truncate font-medium flex-1"
                                             title={form.file_name}>
                                             {form.file_name}
                                         </h3>
                                         {user.user_type === 'admin' && (
                                             <button
                                                 onClick={() => handleEditFileName(form)}
                                                 className="p-1 text-gray-400 hover:text-blue-500
                                                         dark:text-gray-500 dark:hover:text-blue-400
                                                         mt-1"
                                                 title="Edit File Name"
                                             >
                                                 <Pencil className="w-3 h-3" />
                                             </button>
                                         )}
                                     </>
                                 )}
                            </div>
                        </div>

                        {/* Form Comments */}
                        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-md mb-4">
                            {editingCommentId === form.id ? (
                                <div className="flex flex-col gap-2">
                                    <textarea
                                        value={editingComment}
                                        onChange={(e) => setEditingComment(e.target.value)}
                                        className="w-full p-2 text-sm border rounded-md
                                                 dark:bg-gray-800 dark:border-gray-600
                                                 dark:text-gray-300 focus:outline-none
                                                 focus:ring-2 focus:ring-blue-500"
                                        rows={2}
                                    />
                                    <div className="flex gap-2 justify-end">
                                        <button
                                            onClick={() => handleSaveComment(form.id)}
                                            className="p-1 text-green-600 hover:text-green-700
                                                     dark:text-green-500 dark:hover:text-green-400"
                                            title="Save"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="p-1 text-gray-500 hover:text-gray-600
                                                     dark:text-gray-400 dark:hover:text-gray-300"
                                            title="Cancel"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                 <div className="flex justify-between items-start gap-2">
                                     <p className="text-sm text-gray-600 dark:text-gray-300">
                                         {form.document_comments || "No comments"}
                                     </p>
                                     {user.user_type === 'admin' && (
                                         <button
                                             onClick={() => handleEditComment(form)}
                                             className="p-1 text-gray-400 hover:text-blue-500
                                                     dark:text-gray-500 dark:hover:text-blue-400"
                                             title="Edit Comments"
                                         >
                                             <Pencil className="w-4 h-4" />
                                         </button>
                                     )}
                                 </div>
                             )}
                        </div>

                        {/* Download Button */}
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                            <a
                                target={"_blank"}
                                href={form.url}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                                         text-sm font-medium text-blue-600 dark:text-blue-400
                                         bg-blue-50 dark:bg-blue-900/20 rounded-md
                                         hover:bg-blue-100 dark:hover:bg-blue-900/30
                                         transition-colors"
                                title="Download Form"
                            >
                                <Download className="w-4 h-4"/>
                                Download Form
                            </a>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FormList;
